'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Link from 'next/link';
import { fetchCars, createReservation, fetchSettings } from '@/lib/supabase/dinauto-service';
import type { Car } from '@/lib/supabase/dinauto-service';
import { Users, Zap, Fuel, CheckCircle, Info } from 'lucide-react';

interface AddOnDef {
  id: string;
  name: string;
  pricePerDay: number;
  seasonal: boolean;
}

const ADD_ON_DEFS: Omit<AddOnDef, 'pricePerDay'>[] = [
  { id: 'winter-tires', name: 'Winter tires / studded tires', seasonal: true },
  { id: 'gps', name: 'GPS navigation', seasonal: false },
  { id: 'child-seat', name: 'Child seat (0–4 years)', seasonal: false },
  { id: 'booster-seat', name: 'Booster seat (4–10 years)', seasonal: false },
  { id: 'extra-driver', name: 'Extra driver', seasonal: false },
  { id: 'roof-box', name: 'Roof box', seasonal: false },
  { id: 'full-insurance', name: 'Full insurance (zero deductible)', seasonal: false },
];

const ADDON_SETTING_KEYS: Record<string, string> = {
  'winter-tires': 'addon_winter_tires',
  'gps': 'addon_gps',
  'child-seat': 'addon_child_seat',
  'booster-seat': 'addon_booster_seat',
  'extra-driver': 'addon_extra_driver',
  'roof-box': 'addon_roof_box',
  'full-insurance': 'addon_full_insurance',
};

const FALLBACK_PRICES: Record<string, number> = {
  'winter-tires': 80, 'gps': 50, 'child-seat': 40,
  'booster-seat': 30, 'extra-driver': 60, 'roof-box': 70, 'full-insurance': 120,
};

interface FormValues {
  fullName: string;
  email: string;
  phone: string;
  address: string;
  postalCode: string;
  city: string;
  carId: string;
  visitDate: string;
  visitTime: string;
  pickupDate: string;
  returnDate: string;
  confirmed: boolean;
  marketingConsent: boolean;
}

function diffDays(a: string, b: string): number {
  if (!a || !b) return 0;
  const ms = new Date(b).getTime() - new Date(a).getTime();
  return Math.max(0, Math.round(ms / 86400000));
}

function generateBookingRef(): string {
  const year = new Date().getFullYear();
  const num = Math.floor(Math.random() * 9000) + 1000;
  return `DA-${year}-${num}`;
}

export default function ReservationClient() {
  const searchParams = useSearchParams();
  const preselectedCarId = searchParams.get('carId') || '';

  const [cars, setCars] = useState<Car[]>([]);
  const [loadingCars, setLoadingCars] = useState(true);
  const [addOns, setAddOns] = useState<AddOnDef[]>([]);
  const [discount7, setDiscount7] = useState(10);
  const [discount14, setDiscount14] = useState(15);
  const [companyPhone, setCompanyPhone] = useState('+47 4412 673 82');
  const [companyEmail, setCompanyEmail] = useState('info@din-auto.no');
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [submittedBookingRef, setSubmittedBookingRef] = useState('');
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    Promise.all([fetchCars(), fetchSettings()]).then(([carsData, settings]) => {
      setCars(carsData);
      // Build add-ons with live prices
      const liveAddOns: AddOnDef[] = ADD_ON_DEFS.map((def) => ({
        ...def,
        pricePerDay: parseInt(settings[ADDON_SETTING_KEYS[def.id]] || '') || FALLBACK_PRICES[def.id],
      }));
      setAddOns(liveAddOns);
      setDiscount7(parseInt(settings['discount_7_days_percent'] || '') || 10);
      setDiscount14(parseInt(settings['discount_14_days_percent'] || '') || 15);
      setCompanyPhone(settings['company_phone'] || '+47 4412 673 82');
      setCompanyEmail(settings['company_email'] || 'info@din-auto.no');
      setLoadingCars(false);
    });
  }, []);

  const availableCars = useMemo(() => cars.filter((c) => c.status !== 'Hidden' && c.status !== 'Reserved'), [cars]);

  const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      carId: preselectedCarId,
      phone: '+47 ',
    },
  });

  const watchedCarId = watch('carId');
  const watchedPickup = watch('pickupDate');
  const watchedReturn = watch('returnDate');

  const selectedCar: Car | undefined = useMemo(
    () => cars.find((c) => c.id === watchedCarId),
    [watchedCarId, cars]
  );

  const days = useMemo(() => diffDays(watchedPickup, watchedReturn), [watchedPickup, watchedReturn]);
  const carSubtotal = (selectedCar?.pricePerDay || 0) * days;
  const discountPercent = days >= 14 ? discount14 : days >= 7 ? discount7 : 0;
  const discountAmount = Math.round(carSubtotal * discountPercent / 100);
  const addOnsSubtotal = selectedAddOns.reduce((sum, id) => {
    const addon = addOns.find((a) => a.id === id);
    return sum + (addon ? addon.pricePerDay * days : 0);
  }, 0);
  const totalPrice = carSubtotal - discountAmount + addOnsSubtotal;

  const toggleAddOn = (id: string) => {
    setSelectedAddOns((prev) => prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]);
  };

  const onSubmit = async (data: FormValues) => {
    if (!selectedCar) { toast.error('Please select a car'); return; }
    setIsSubmitting(true);
    const bookingRef = generateBookingRef();
    const addOnsData = selectedAddOns.map((id) => {
      const addon = addOns.find((a) => a.id === id)!;
      return { id: addon.id, name: addon.name, pricePerDay: addon.pricePerDay, days, subtotal: addon.pricePerDay * days };
    });
    const result = await createReservation({
      bookingRef,
      fullName: data.fullName, email: data.email, phone: data.phone,
      address: data.address || '', postalCode: data.postalCode || '', city: data.city || '',
      carId: selectedCar.id, carModel: selectedCar.model, carBrand: selectedCar.brand,
      carCategory: selectedCar.category, pricePerDay: selectedCar.pricePerDay,
      visitDateTime: `${data.visitDate}T${data.visitTime}`,
      pickupDate: data.pickupDate, returnDate: data.returnDate, days,
      addOns: addOnsData, carSubtotal, addOnsSubtotal, discountPercent, discountAmount, totalPrice,
      status: 'Pending',
      marketingConsent: data.marketingConsent ?? false,
    });
    setIsSubmitting(false);
    if (result) {
      setSubmittedBookingRef(bookingRef);
      setSubmittedEmail(data.email);
      setSubmitted(true);
      toast.success('Reservation submitted! We will contact you within 24 hours.');
    } else {
      toast.error('Failed to submit reservation. Please try again.');
    }
  };

  const daysUntilDiscount7 = selectedCar && days > 0 && days < 7 ? 7 - days : null;
  const daysUntilDiscount14 = selectedCar && days >= 7 && days < 14 ? 14 - days : null;

  if (submitted) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-16">
        <div className="max-w-2xl mx-auto bg-white border border-border p-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-12 bg-green-100 flex items-center justify-center">
              <CheckCircle size={28} className="text-green-600" />
            </div>
            <div>
              <h2 className="font-black text-2xl" style={{ fontWeight: 900 }}>Reservation Submitted!</h2>
              <p className="text-muted-foreground text-sm">Booking reference: {submittedBookingRef}</p>
            </div>
          </div>
          <div className="bg-muted p-5 mb-6 space-y-3 text-sm">
            <p className="font-700">Summary</p>
            <div className="grid grid-cols-2 gap-2 text-muted-foreground">
              <span>Car</span><span className="font-600 text-foreground">{selectedCar?.brand} {selectedCar?.model}{selectedCar?.year ? ` ${selectedCar.year}` : ''}</span>
              <span>Pickup</span><span className="font-600 text-foreground">{watchedPickup}</span>
              <span>Return</span><span className="font-600 text-foreground">{watchedReturn}</span>
              <span>Days</span><span className="font-600 text-foreground">{days}</span>
              <span>Total</span><span className="font-black text-foreground text-base tabular-nums">{totalPrice.toLocaleString('no-NO')} NOK</span>
            </div>
          </div>
          <p className="text-muted-foreground text-sm leading-relaxed mb-6">
            We will contact you at <strong>{submittedEmail}</strong> to confirm your reservation. If you have questions, call us at <a href={`tel:${companyPhone.replace(/\s/g, '')}`} className="text-foreground font-600 hover:underline">{companyPhone}</a> or email <a href={`mailto:${companyEmail}`} className="text-foreground font-600 hover:underline">{companyEmail}</a>.
          </p>
          <button onClick={() => setSubmitted(false)} className="btn-outline">
            Make Another Reservation
          </button>
        </div>
      </div>
    );
  }

  if (loadingCars) {
    return (
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 text-center">
        <div className="inline-block w-8 h-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mb-4" />
        <p className="text-muted-foreground">Loading available cars...</p>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-10">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Left: Form */}
          <div className="xl:col-span-2 space-y-8">

            {/* Section 1: Personal details */}
            <div className="bg-white border border-border p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="bg-foreground text-primary font-black text-sm w-7 h-7 flex items-center justify-center" style={{ fontWeight: 900 }}>1</span>
                <h2 className="font-black text-xl" style={{ fontWeight: 800 }}>Personal Details</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="label-text">Full Name *</label>
                  <input {...register('fullName', { required: 'Full name is required' })} className={`input-field ${errors.fullName ? 'input-error' : ''}`} placeholder="Erik Andersen" />
                  {errors.fullName && <p className="text-red-500 text-xs mt-1">{errors.fullName.message}</p>}
                </div>
                <div>
                  <label className="label-text">Email Address *</label>
                  <input {...register('email', { required: 'Email is required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' } })} type="email" className={`input-field ${errors.email ? 'input-error' : ''}`} placeholder="erik@example.com" />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>
                <div>
                  <label className="label-text">Phone Number *</label>
                  <p className="text-muted-foreground text-xs mb-1.5">Norwegian format: +47 XX XXX XXXX</p>
                  <input {...register('phone', { required: 'Phone number is required' })} type="tel" className={`input-field ${errors.phone ? 'input-error' : ''}`} placeholder="+47 91 234 567" />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label className="label-text">Street Address</label>
                  <input {...register('address')} className="input-field" placeholder="Storgata 14" />
                </div>
                <div>
                  <label className="label-text">Postal Code</label>
                  <input {...register('postalCode')} className="input-field" placeholder="0184" maxLength={4} />
                </div>
                <div>
                  <label className="label-text">City</label>
                  <input {...register('city')} className="input-field" placeholder="Oslo" />
                </div>
              </div>
            </div>

            {/* Section 2: Car & Dates */}
            <div className="bg-white border border-border p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="bg-foreground text-primary font-black text-sm w-7 h-7 flex items-center justify-center" style={{ fontWeight: 900 }}>2</span>
                <h2 className="font-black text-xl" style={{ fontWeight: 800 }}>Car & Dates</h2>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="label-text">Select Car *</label>
                  <p className="text-muted-foreground text-xs mb-1.5">Reserved cars are excluded from this list</p>
                  <select {...register('carId', { required: 'Please select a car' })} className={`input-field ${errors.carId ? 'input-error' : ''}`}>
                    <option value="">Choose a car...</option>
                    {(['Economy', 'Comfort', 'SUV'] as const).map((cat) => (
                      <optgroup key={`optgroup-${cat}`} label={`— ${cat} —`}>
                        {availableCars.filter((c) => c.category === cat).map((car) => (
                          <option key={car.id} value={car.id}>
                            {car.brand} {car.model}{car.year ? ` ${car.year}` : ''} — {car.pricePerDay.toLocaleString('no-NO')} NOK/day ({car.seats} seats, {car.gearbox}, {car.fuel})
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {errors.carId && <p className="text-red-500 text-xs mt-1">{errors.carId.message}</p>}
                </div>
                {selectedCar && (
                  <div className="bg-muted border border-border p-4 flex items-center gap-4">
                    <div className="flex gap-4 text-sm flex-wrap">
                      <span className="flex items-center gap-1.5 text-muted-foreground"><Users size={13} />{selectedCar.seats} seats</span>
                      <span className="flex items-center gap-1.5 text-muted-foreground"><Zap size={13} />{selectedCar.gearbox}</span>
                      <span className="flex items-center gap-1.5 text-muted-foreground"><Fuel size={13} />{selectedCar.fuel}</span>
                      <span className="font-black text-foreground tabular-nums" style={{ fontWeight: 800 }}>{selectedCar.pricePerDay.toLocaleString('no-NO')} NOK/day</span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-text">Visit Date *</label>
                    <p className="text-muted-foreground text-xs mb-1.5">When you come to our office to pick up the car</p>
                    <input {...register('visitDate', { required: 'Visit date is required' })} type="date" className={`input-field ${errors.visitDate ? 'input-error' : ''}`} />
                    {errors.visitDate && <p className="text-red-500 text-xs mt-1">{errors.visitDate.message}</p>}
                  </div>
                  <div>
                    <label className="label-text">Visit Time *</label>
                    <p className="text-muted-foreground text-xs mb-1.5">Opening hours: Mon–Fri 08–18, Sat 09–15</p>
                    <input {...register('visitTime', { required: 'Visit time is required' })} type="time" className={`input-field ${errors.visitTime ? 'input-error' : ''}`} />
                    {errors.visitTime && <p className="text-red-500 text-xs mt-1">{errors.visitTime.message}</p>}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-text">Pickup Date *</label>
                    <input {...register('pickupDate', { required: 'Pickup date is required' })} type="date" className={`input-field ${errors.pickupDate ? 'input-error' : ''}`} />
                    {errors.pickupDate && <p className="text-red-500 text-xs mt-1">{errors.pickupDate.message}</p>}
                  </div>
                  <div>
                    <label className="label-text">Return Date *</label>
                    <input {...register('returnDate', { required: 'Return date is required', validate: (v) => !watchedPickup || v > watchedPickup || 'Return must be after pickup' })} type="date" className={`input-field ${errors.returnDate ? 'input-error' : ''}`} />
                    {errors.returnDate && <p className="text-red-500 text-xs mt-1">{errors.returnDate.message}</p>}
                  </div>
                </div>
                {days > 0 && (
                  <div className="flex items-center gap-2 bg-muted px-4 py-2.5 text-sm flex-wrap">
                    <Info size={14} className="text-primary" />
                    <span>Rental period: <strong>{days} day{days !== 1 ? 's' : ''}</strong></span>
                    {daysUntilDiscount7 && <span className="ml-2 text-amber-600 font-600">Rent {daysUntilDiscount7} more day{daysUntilDiscount7 !== 1 ? 's' : ''} and save {discount7}%!</span>}
                    {daysUntilDiscount14 && <span className="ml-2 text-amber-600 font-600">Rent {daysUntilDiscount14} more day{daysUntilDiscount14 !== 1 ? 's' : ''} and save {discount14}%!</span>}
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Add-ons */}
            <div className="bg-white border border-border p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="bg-foreground text-primary font-black text-sm w-7 h-7 flex items-center justify-center" style={{ fontWeight: 900 }}>3</span>
                <h2 className="font-black text-xl" style={{ fontWeight: 800 }}>Optional Add-ons</h2>
              </div>
              <div className="space-y-3">
                {addOns.map((addon) => {
                  const checked = selectedAddOns.includes(addon.id);
                  return (
                    <label key={`addon-${addon.id}`} className={`flex items-center gap-4 p-4 border cursor-pointer transition-colors ${checked ? 'border-foreground bg-foreground/3' : 'border-border hover:border-foreground/40'}`}>
                      <input type="checkbox" checked={checked} onChange={() => toggleAddOn(addon.id)} className="w-4 h-4 accent-foreground" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-700 text-sm">{addon.name}</span>
                          {addon.seasonal && <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5">Recommended Nov–Apr</span>}
                        </div>
                        {days > 0 && checked && <p className="text-xs text-muted-foreground mt-0.5">{addon.pricePerDay} NOK/day × {days} days = {(addon.pricePerDay * days).toLocaleString('no-NO')} NOK</p>}
                      </div>
                      <span className="font-black text-sm tabular-nums" style={{ fontWeight: 800 }}>+{addon.pricePerDay} NOK<span className="font-400 text-muted-foreground text-xs">/day</span></span>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Section 4: Submit */}
            <div className="bg-white border border-border p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="bg-foreground text-primary font-black text-sm w-7 h-7 flex items-center justify-center" style={{ fontWeight: 900 }}>4</span>
                <h2 className="font-black text-xl" style={{ fontWeight: 800 }}>Confirm &amp; Submit</h2>
              </div>

              {/* Required confirmation checkbox */}
              <label className="flex items-start gap-3 cursor-pointer mb-3">
                <input {...register('confirmed', { required: 'Please confirm your details are correct' })} type="checkbox" className="w-4 h-4 mt-0.5 accent-foreground" />
                <span className="text-sm text-muted-foreground">
                  I confirm that all the information provided above is correct and I accept the{' '}
                  <Link href="/terms" className="text-foreground font-bold underline hover:text-primary transition-colors">
                    Terms &amp; Conditions
                  </Link>
                  .
                </span>
              </label>
              {errors.confirmed && <p className="text-red-500 text-xs mb-4">{errors.confirmed.message}</p>}

              {/* Optional marketing consent checkbox */}
              <label className="flex items-start gap-3 cursor-pointer mb-6 mt-4">
                <input {...register('marketingConsent')} type="checkbox" className="w-4 h-4 mt-0.5 accent-foreground" />
                <span className="text-sm text-muted-foreground">
                  I agree to receive notifications, offers, and updates from DIN AUTO AS via email and SMS.{' '}
                  <span className="text-xs text-muted-foreground/70">
                    You can unsubscribe at any time. See our{' '}
                    <Link href="/privacy-policy" className="text-foreground underline hover:text-primary transition-colors">
                      Privacy Policy
                    </Link>
                    .
                  </span>
                </span>
              </label>

              <button type="submit" disabled={isSubmitting} className="btn-primary w-full justify-center py-4 text-base gap-3">
                {isSubmitting ? (<><span className="w-4 h-4 border-2 border-foreground/30 border-t-foreground rounded-full animate-spin" />Sending...</>) : 'Send reservasjon'}
              </button>
              <p className="text-xs text-muted-foreground text-center mt-3">By submitting you agree to be contacted at the email and phone number provided.</p>
            </div>
          </div>

          {/* Right: Price Summary */}
          <div className="xl:col-span-1">
            <div className="bg-white border border-border p-6 sticky top-24">
              <h3 className="font-black text-lg mb-5 pb-4 border-b border-border" style={{ fontWeight: 900 }}>Price Summary</h3>
              {!selectedCar || days === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground text-sm">Select a car and dates to see the price breakdown.</p>
                </div>
              ) : (
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{selectedCar.brand} {selectedCar.model}{selectedCar.year ? ` ${selectedCar.year}` : ''}</span>
                    <span className="font-600 tabular-nums">{selectedCar.pricePerDay.toLocaleString('no-NO')} NOK/day</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">× {days} days</span>
                    <span className="font-700 tabular-nums">{carSubtotal.toLocaleString('no-NO')} NOK</span>
                  </div>
                  {discountPercent > 0 && (
                    <div className="flex justify-between text-green-600 font-700">
                      <span>{discountPercent === discount14 ? `Long-term discount −${discount14}%` : `Weekly discount −${discount7}%`}</span>
                      <span className="tabular-nums">−{discountAmount.toLocaleString('no-NO')} NOK</span>
                    </div>
                  )}
                  {selectedAddOns.length > 0 && (
                    <div className="pt-2 border-t border-border">
                      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Add-ons</p>
                      {selectedAddOns.map((id) => {
                        const addon = addOns.find((a) => a.id === id);
                        if (!addon) return null;
                        return (
                          <div key={`summary-addon-${id}`} className="flex justify-between mb-1.5">
                            <span className="text-muted-foreground text-xs">{addon.name}</span>
                            <span className="font-600 text-xs tabular-nums">{(addon.pricePerDay * days).toLocaleString('no-NO')} NOK</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div className="pt-4 border-t-2 border-foreground flex justify-between items-end">
                    <div>
                      <p className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Total</p>
                      <p className="text-xs text-muted-foreground">incl. all fees</p>
                    </div>
                    <p className="text-3xl font-black tabular-nums" style={{ fontWeight: 900 }}>
                      {totalPrice.toLocaleString('no-NO')}<span className="text-base font-600 text-muted-foreground"> NOK</span>
                    </p>
                  </div>
                  <div className="bg-muted p-3 mt-2 space-y-1 text-xs text-muted-foreground">
                    <p>✓ Free cancellation up to 24h before pickup</p>
                    <p>✓ No hidden fees — this is your final price</p>
                    {discountPercent === 0 && days > 0 && days < 7 && (
                      <p className="text-amber-600 font-600">Rent {7 - days} more day{7 - days !== 1 ? 's' : ''} to save {discount7}%!</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}